import {
  Fragment,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Listbox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { useField, useFormikContext } from 'formik';
import { useRequestTypes } from '../../../hooks/useRequestTypes';
import { useGlobalState } from '../../../contexts/GlobalStateProvider';

interface Props {
  name: string;
  label: string;
}

export interface RequestTypeSelectRef {
  reset: () => void;
}

export const RequestTypeSelect = forwardRef<RequestTypeSelectRef, Props>(
  ({ name, label }, ref) => {
    const [field, meta, helpers] = useField(name);
    const [selected, setSelected] = useState<{ id: string; name: string }[]>(
      []
    );
    const { errors, touched } = useFormikContext<{ [key: string]: string }>();
    const { currentClient } = useGlobalState();
    const { data: requestTypes = [] } = useRequestTypes(
      currentClient ?? undefined
    );

    const options = requestTypes.map((type) => ({
      id: type.RequestType,
      name: type.RequestTypeName,
    }));

    // Expose the reset function to parent components
    useImperativeHandle(ref, () => ({
      reset: () => {
        setSelected([]);
        helpers.setValue([]);
      },
    }));

    useEffect(() => {
      helpers.setValue(selected?.map((x) => x.id));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    // Custom handler to properly toggle selection
    const handleSelectionChange = (value: { id: string; name: string }) => {
      setSelected((current) => {
        // Check if item is already selected
        const isSelected = current.some((item) => item.id === value.id);

        if (isSelected) {
          // Remove if already selected
          return current.filter((item) => item.id !== value.id);
        } else {
          // Add if not already selected
          return [...current, value];
        }
      });
    };

    return (
      <div className="col-span-1">
        <Listbox value={selected} onChange={setSelected} name={name} multiple>
          {({ open }) => (
            <>
              <Listbox.Label className="block text-sm font-medium leading-6 text-gray-900">
                {label}
              </Listbox.Label>
              <div className="relative mt-2">
                <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                  <span className="block truncate">
                    {selected.length === 0
                      ? 'Please select a request type'
                      : selected.map((x) => x.name).join(', ')}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>

                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {options.map((option, i) => {
                      const isOptionSelected = selected.some(
                        (item) => item.id === option.id
                      );
                      return (
                        <div
                          key={`referral-request-type-${i}`}
                          className={classNames(
                            'relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-indigo-100',
                            isOptionSelected ? 'bg-indigo-50' : 'bg-white'
                          )}
                          onClick={() => handleSelectionChange(option)}
                        >
                          <span className="flex items-center">
                            <span
                              className={classNames(
                                'block truncate',
                                isOptionSelected
                                  ? 'font-semibold'
                                  : 'font-normal'
                              )}
                            >
                              {option.name}
                            </span>
                          </span>

                          {isOptionSelected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )}
        </Listbox>
        {errors[name] && touched[name] ? (
          <p className="mt-2 text-sm text-red-600" id="email-error">
            {errors[name]}
          </p>
        ) : null}
      </div>
    );
  }
);
